import React from 'react'
import { clsx } from 'clsx'

const AgentCard = ({ agent, index }: { agent: any; index: number }) => {
  return (
    <div className="bg-slate-50 pt-px rounded-2xl max-w-4xl">
      <div className="px-4 py-10 sm:px-10 sm:py-16 md:py-20 lg:px-20 lg:py-32">
        <h2
          className="inline-flex items-center rounded-full px-4 py-1 text-blue-600 ring-1 ring-inset ring-blue-600"
          id="author-title"
        >
          <span className="font-mono text-sm" aria-hidden="true">
            {'0' + index.toString()}
          </span>
          <span className="ml-3 h-3.5 w-px bg-blue-600/20"></span>
          <span className="ml-3 text-base font-medium tracking-tight">For {agent.actor}</span>
        </h2>
        <p className="mt-8 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          <span className="block text-blue-600">{agent.title} –</span> {agent.description}
        </p>
        <p className="mt-4 text-lg tracking-tight text-slate-700">{agent.longDescription}</p>
        <p className="mt-8">
          <div className="flex flex-col text-base font-medium tracking-tight">
            <div className="ml-4 text-blue-600">I can answer questions like:</div>
            <div className="">
              <ul className="mt-4 ml-8 list-disc list-inside text-slate-700">
                {agent.prompts.map((prompt: string, index: number) => (
                  <li key={index} className="mt-2">
                    {prompt}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </p>
      </div>
    </div>
  )
}

const Hero = () => {
  const [currentIndex, setCurrentIndex] = React.useState<number>(0)
  const timeoutId = React.useRef<NodeJS.Timeout | null>(null)

  const agents: any = [
    {
      title: 'LookML Assistant',
      description: "I'm really good at writing LookML and finding bugs in your code.",
      actor: 'Developers',
      longDescription:
        'Pair program with a co-pilot that knows about the LookML for your company as well as all the best practices and patterns. By recording walkthroughs you can teach me how you like to setup your projects, and I can help find bugs in your code.',
      prompts: [
        'what are our guidelines for writing views',
        'how many different measures do we have defined',
        'do we use view_label anywhere',
        'take this table schema and generate a view based on our guidelines',
      ],
    },
    {
      title: 'Administration Expert',
      description: 'I know the best way to setup a Looker instance, let me setup yours.',
      longDescription:
        'Save time by automating a lot of the manual work of setting up a Looker instance. Not only can you institutionalize best practices, you can automate the checks with our workflows.',
      actor: 'Looker Admins',
      prompts: [
        'what beta features should I enable',
        'do i have timezone setup correctly in my instance',
        'give me a review of my database connections',
        'do I have any unused dashboards',
        'are users having poor performance',
      ],
    },
    {
      title: 'Onboarding Specialist',
      description: "I've been trained to onboard new users and help them get started.",
      longDescription:
        'Get new users up to speed quickly by having a co-pilot onboard them. I can help them get setup with their own personal dashboards, and teach them how to use Looker.',
      actor: 'Dashboard Viewers',
      prompts: [
        'what dashboards should I use if in marketing',
        'how do I create a dashboard',
        'where are the boards for my team',
        'what is a measure',
        'how do I do period over period on the attribution dashboard',
      ],
    },
    {
      title: 'Ad Spend Optimizer',
      description: 'I can help you dig into your paid campaigns.',
      longDescription:
        "I don't just answer questions about Looker. Since all your company data is in your instance, I can help reason about your business. I can help you dig into your paid campaigns, and help you optimize your spend.",
      actor: 'Marketing Analysts',
      prompts: [
        'what was our CPA last week',
        'what is our CAC for the last 3 months',
        'which channel is performing the best',
        'what is our contributing the most to our ROAS for the last 3 months',
      ],
    },
    {
      title: 'Board Meeting Assistant',
      description: 'I can answer high level questions about your business.',
      longDescription:
        "I can answer high level questions about your business. No need to actually login to your Looker instance. I'm like your dedicated analyst at your beck and call.",
      actor: 'C-Level Execs',
      prompts: [
        'what is our revenue for the last 3 months',
        'how are my projects doing',
        'have we been profitable for the last 3 months',
        'are we on track to hit our goals for the quarter',
      ],
    },
  ]

  const handleNextAgent = () => {
    const nextIndex = (currentIndex + 1) % agents.length
    setCurrentIndex(nextIndex)
  }

  const handlePreviousAgent = () => {
    const previousIndex = (currentIndex - 1 + agents.length) % agents.length
    setCurrentIndex(previousIndex)
  }

  React.useEffect(() => {
    // Set the automatic cycling
    timeoutId.current = setTimeout(handleNextAgent, 10000) // change cards every 5 seconds

    // Cleanup function to clear the timeout when the component is unmounted
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
      }
    }
  }, [currentIndex])

  return (
    <div className="relative px-6 pt-14 lg:px-8">
      <div className="pt-12 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-2xl text-center mb-20">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
            Looker Co-Pilots trained on your instance and data
          </h1>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Boost your productivity by making your personalized agent do the Looker legwork for you.
            Create an agent for each team, project, or workflow.
          </p>
        </div>

        <div className="relative">
          <div
            className="relative mx-auto max-w-3xl flex list-none justify-center p-0"
            data-te-carousel-indicators
          >
            <span className="absolute left-0 h-8 w-8">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                onClick={handlePreviousAgent}
                className="h-6 w-6 text-gray-400 hover:text-gray-500 cursor-pointer"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                ></path>
              </svg>
            </span>
            {agents.map((agent: any, index: number) => (
              <button
                key={index}
                type="button"
                data-te-target="#carouselExampleCaptions"
                data-te-slide-to="0"
                data-te-carousel-active
                className={clsx(
                  index == currentIndex ? 'bg-gray-500' : 'bg-gray-300 hover:bg-gray-400',
                  'mx-[3px] box-content h-[3px] w-[30px] flex-initial cursor-pointer border-0 border-y-[10px] border-solid border-transparent bg-clip-padding p-0 -indent-[999px] opacity-50 transition-opacity duration-[600ms] ease-[cubic-bezier(0.25,0.1,0.25,1.0)] motion-reduce:transition-none'
                )}
                aria-current="true"
                aria-label={agent.label}
                onClick={() => setCurrentIndex(index)}
              ></button>
            ))}
            <span className="absolute right-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="h-6 w-6 text-gray-400 hover:text-gray-500 cursor-pointer"
                onClick={handleNextAgent}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                ></path>
              </svg>
            </span>
          </div>
          <div className="relative mt-5 px-4 sm:px-0 md:px-14 py-4 sm:py-8 md:py-16 xl:px-16">
            <div className="min-h-[800px] max-w-3xl mx-auto relative ">
              {' '}
              {/* Placeholder */}
              {agents.map((agent: any, index: number) => (
                <div
                  key={index}
                  className={`absolute top-0 left-0 w-full transition-opacity duration-1000 ${
                    currentIndex === index ? 'opacity-100 visible' : 'opacity-0 invisible'
                  }`}
                >
                  <AgentCard agent={agent} index={index + 1} />
                </div>
              ))}
            </div>
          </div>

          <div className="absolute right-[50%] -top-10 z-10">
            <div className="absolute left-3 w-28 -top-5 font-fuzzy text-right text-ddu-orange-500 font-bold text-xl">
              Check them out
            </div>
            <svg
              className="font-bold -scale-x-100"
              width="23"
              height="49"
              viewBox="0 0 23 49"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.6306 47.3189L18.15 46.7244C18.425 46.6994 18.6683 46.902 18.6933 47.177C18.7184 47.452 18.5158 47.6952 18.2408 47.7203L10.8385 48.3953C10.6882 48.409 10.5398 48.3541 10.4346 48.2459C10.3294 48.1378 10.2787 47.9879 10.2966 47.8381L11.1199 40.9408C11.1527 40.6666 11.4015 40.4709 11.6757 40.5036C11.9499 40.5363 12.1456 40.7851 12.1129 41.0593L11.5006 46.1887C18.4163 41.2076 22.0292 32.3064 20.9413 23.6106C19.8239 14.6791 13.7389 5.93971 1.08343 1.95669C0.820022 1.87379 0.673695 1.59305 0.756596 1.32964C0.839497 1.06624 1.12023 0.919912 1.38364 1.00281C14.3948 5.09779 20.7654 14.149 21.9336 23.4865C23.0866 32.7025 19.1634 42.1737 11.6306 47.3189Z"
                fill="rgb(234 106 41)"
              ></path>
            </svg>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Hero
